import { Component, OnInit, ElementRef } from '@angular/core';
import { PageBase, PriceModel, CurrencyPipe } from 't4core';
import { NavigationService } from 'app-components';
import { AppIntegrationService, AppSettingsService } from 't4-app-integration';
import { BookingService } from 'app-components';
import { ActiveRentalService } from 'app-components';

@Component({
    selector: 'app-payment',
    templateUrl: './payment.component.html',
    standalone: false
})
export class PaymentComponent extends PageBase implements OnInit {
  private rentalId: number;
  private autoRedirect: boolean = true;
  private returnPath: string = "";
  private suppressAutoRedirect: boolean = false;

  private flow: string = "";
  private readonly pendingPaymentConfirmationAttempts: number = 5;
  private readonly pendingPaymentConfirmationDelayMs: number = 3000;

  public cost: PriceModel = null;
  public amount: string = "";

  public returnAction: string = "";
  public paymentFailed: boolean = false;
  public redirecting: boolean = false;
  public loading: boolean = false;
  public paymentCancelled: boolean = false;
  public showVerifySpinner: boolean = false;
  public verifyProgressCount: number = 0;
  private paymentAttemptInProgress: boolean = false;
  private readonly verifyProgressMaxSeconds: number = 15;
  private verifyProgressIntervalId: ReturnType<typeof setInterval> | null = null;

  constructor(private navigator: NavigationService, el: ElementRef, public appSettings: AppSettingsService, private bookingService: BookingService, private appService: AppIntegrationService, private BookingDataService: BookingService, private rentalService: ActiveRentalService) {
    super(el);

    // Get parameters
    this.rentalId = parseInt(this.navigator.getParam("rentalId"));
    this.autoRedirect = this.navigator.getParam("autoRedirect") == "true";
    this.returnPath = this.navigator.getParam("returnPath");
    this.flow = this.navigator.getParam("flow");
    this.suppressAutoRedirect = this.navigator.getParam("suppressAutoRedirect") == "true";

    if (this.navigator.getParam("changeBooking") == "true" && !isNaN(this.rentalId)) {
      this.bookingService.startChangeBookingFlow(this.rentalId);
    }

    if (this.bookingService.isChangeBookingFlowActive()) {
      this.bookingService.showChangeBookingBanner(false);
    }

    // Has the user returned from the payment flow?
    this.returnAction = this.navigator.getParam("action"); // So where is this param (supposed to be) set?
    if (this.returnAction) {
      void this.handlePaymentReturn();
    }
    // Prepare the payment UI
    else if (this.autoRedirect && !this.suppressAutoRedirect) {
      this.redirect();
    }
  }

  async ngOnInit() {
    this.cost = await this.Api.get<PriceModel>("/Price/GetRentalCost", { id: this.rentalId });
    this.amount = new CurrencyPipe(this.Settings).transform((this.cost as any).ToPay)
  }

  public async retry() {
    await this.redirect();
  }

  private async handlePaymentReturn() {
    this.Insights.trackTrace("Payment returned with action", { action: this.returnAction.toString(), flow: this.flow, rentalId: this.rentalId.toString() });

    this.appSettings.params["action"] = null;
    this.appSettings.pack();

    if (this.flow == "Booking") {
      await this.bookingService.restartSession(this.rentalId);
      if (this.returnAction === "accept") {
        this.navigator.executeCommand("Success", { rentalId: this.rentalId });
      }
      else {
        this.paymentFailed = true;
      }

      return;
    }

    await this.rentalService.setActiveRental(this.rentalId);
    if (this.returnAction === "accept") {
      const paymentConfirmed = await this.confirmPendingPayment();
      if (paymentConfirmed) {
        this.navigator.executeCommand("Success", { rentalId: this.rentalId });
      }
      else {
        this.paymentFailed = true;
        await this.UI.alert("Intellitrailer.Booking.UnconfirmedBooking.PaymentFailed", "Payment Failed", "Intellitrailer.Booking.UnconfirmedBooking.PaymentFailedMsg", "The payment could not be verified. Please try again.");
      }
    }
    else {
      this.paymentFailed = true;
    }
  }

  private async redirect() {
    // if (this.appSettings.appType == "app" && this.appSettings.meetsMinimumAppVersion(4.10) && this.appSettings.contextId != "OKQ8Unmanned_API") {
    //   console.log("start native dropin");
    //   await this.startNativeDropIn();
    // }
    // else {
      this.redirecting = true;

      // Extract variables
      var theme = this.appSettings.settings.theme;
      var appType = this.appSettings.appType;
      var subcontext = this.appSettings.getParam("subcontext");
      var inStore = this.bookingService.inStore;
      var language = this.appSettings.getParam("language");

      // Build returnPath with existing parameters
      let returnPath = this.returnPath;

      // Append variables if defined
      if (theme !== undefined) {
        returnPath += (returnPath.includes('?') ? '&' : '?') + 'theme=' + encodeURIComponent(theme);
      }
      if (appType !== undefined) {
        returnPath += (returnPath.includes('?') ? '&' : '?') + 'appType=' + encodeURIComponent(appType);
      }
      if (subcontext !== undefined) {
        returnPath += (returnPath.includes('?') ? '&' : '?') + 'subcontext=' + encodeURIComponent(subcontext);
      }
      if (inStore !== undefined) {
        returnPath += (returnPath.includes('?') ? '&' : '?') + 'inStore=' + encodeURIComponent(inStore);
      }
      if (language !== undefined) {
        returnPath += (returnPath.includes('?') ? '&' : '?') + 'language=' + encodeURIComponent(language);
      }

    if (this.bookingService.isChangeBookingFlowActive() && !returnPath.includes('changeBooking=true')) {
      returnPath += (returnPath.includes('?') ? '&' : '?') + 'changeBooking=true';
    }

    if (this.appSettings.userId) {
      returnPath += (returnPath.includes('?') ? '&' : '?') + 'userId=' + this.appSettings.userId;
    }

      this.appSettings.pack();

      // Redirect to the updated returnPath
      this.BookingDataService.redirectToPayment(this.rentalId, returnPath);
    //}
 
  }

  private async startNativeDropIn() {
    try {
      if (this.paymentAttemptInProgress) {
        return;
      }

      this.paymentAttemptInProgress = true;
      this.paymentFailed = false;
      this.paymentCancelled = false;
      this.showVerifySpinner = false;

      const deviceType = AppSettingsService.getDeviceType();
      const normalizedDeviceType = String(deviceType || "").toLowerCase();
      const platform = normalizedDeviceType === "android"
        ? "android"
        : normalizedDeviceType === "ios"
          ? "ios"
          : null;

      if (!platform) {
        this.paymentFailed = true;
        this.Insights.trackTrace("Unsupported device type for native payment", { rentalId: this.rentalId.toString(), flow: this.flow, deviceType: String(deviceType || "") });
        await this.UI.alert("Intellitrailer.Booking.UnconfirmedBooking.PaymentFailed", "Payment Failed", "Intellitrailer.Booking.UnconfirmedBooking.PaymentFailedMsg", "Native payment is not supported on this device. Please try again.");
        return;
      }

      const baseAppScheme = this.appSettings.appScheme ?? this.appSettings.settings.theme;
      const returnUrl = platform === "android" ? `adyencheckout://${baseAppScheme}` : `${baseAppScheme}://Booking/Payment`;

      console.log("returnUrl", returnUrl);
      const sessionResponse = await this.Api.get<any>("Payment/Adyen/CreateSession", { id: this.rentalId, returnUrl, platform });
      this.Insights.trackTrace("Adyen session created", { rentalId: this.rentalId.toString(), flow: this.flow });

      const nativeResult = await this.appService.startAdyenPayment(sessionResponse);
      this.Insights.trackTrace("Adyen native result", { rentalId: this.rentalId.toString(), flow: this.flow, result: JSON.stringify(nativeResult || {}) });

      if (!nativeResult) {
        this.paymentFailed = true;
        return;
      }

      const resultCode = String(nativeResult.resultCode || "").toLowerCase();
      console.log("Payment result", resultCode);
      if (resultCode == "authorised") {
        await this.continue();
        this.paymentFailed = false;
      }
      else if (resultCode === "received" || resultCode === "pending") {
        const paymentConfirmed = await this.confirmPendingPayment();
        if (paymentConfirmed) {
          await this.continue();
          this.paymentFailed = false;
        }
        else {
          this.paymentFailed = true;
          await this.UI.alert("Intellitrailer.Booking.UnconfirmedBooking.PaymentFailed", "Payment Failed", "Intellitrailer.Booking.UnconfirmedBooking.PaymentFailedMsg", "The payment could not be verified. Please try again.");
        }
      }
      else if (resultCode === "cancelled") {
        console.log("cancelled");
        this.paymentCancelled = true;
        var x = await this.UI.alert("Intellitrailer.Booking.UnconfirmedBooking.PaymentCancelled", "Payment Cancelled", "Intellitrailer.Booking.UnconfirmedBooking.PaymentCancelledMsg", "The payment process was cancelled. Please try again.");

      } else {
        console.log("failed");
        this.paymentFailed = true;
        var x = await this.UI.alert("Intellitrailer.Booking.UnconfirmedBooking.PaymentFailed", "Payment Failed", "Intellitrailer.Booking.UnconfirmedBooking.PaymentFailedMsg", "The payment process failed. Please try again.");
      }
    } catch (ex) {
      console.log("exception");
      this.showVerifySpinner = false;
      this.stopVerifyProgress(true);
      this.paymentFailed = true;
      console.error("startNativeDropIn error", ex);
      this.Insights.trackTrace("startNativeDropIn Error", { rentalId: this.rentalId.toString(), error: ex });
    } finally {
      this.paymentAttemptInProgress = false;
    }
  }

  private async confirmPendingPayment(): Promise<boolean> {
    this.showVerifySpinner = true;
    this.verifyProgressCount = 1;
    this.startVerifyProgress();

    try {
      for (let attempt = 0; attempt < this.pendingPaymentConfirmationAttempts; attempt++) {
        await this.delay(this.pendingPaymentConfirmationDelayMs);

        const paymentConfirmed = await this.bookingService.treatPendingPaymentAsConfirmed(this.rentalId);
        console.log("Pending payment confirmation attempt", attempt + 1, paymentConfirmed);

        if (paymentConfirmed) {
          return true;
        }
      }

      return false;
    }
    finally {
      this.showVerifySpinner = false;
      this.stopVerifyProgress(true);
    }
  }

  private async delay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  public get verifyProgressValue(): number {
    if (this.verifyProgressCount <= 0) {
      return 0;
    }

    return Math.max((this.verifyProgressCount / this.verifyProgressMaxSeconds) * 100, 3 + 1.0 / 3.0);
  }

  private startVerifyProgress() {
    this.stopVerifyProgress(true);
    this.verifyProgressCount = 1;
    this.verifyProgressIntervalId = setInterval(() => {
      if (this.verifyProgressCount < this.verifyProgressMaxSeconds) {
        this.verifyProgressCount++;
      }
    }, 1000);
  }

  private stopVerifyProgress(resetCount: boolean = false) {
    if (this.verifyProgressIntervalId) {
      clearInterval(this.verifyProgressIntervalId);
      this.verifyProgressIntervalId = null;
    }

    if (resetCount) {
      this.verifyProgressCount = 0;
    }
  }

  public async continue() {
    if (this.flow == "Booking") {
      console.log("booking flow");
      await this.bookingService.restartSession(this.rentalId);
      this.navigator.executeCommand("Success", { rentalId: this.rentalId });
    } else {
      console.log("rental flow");
      await this.rentalService.setActiveRental(this.rentalId);
      this.navigator.executeCommand("Success", { rentalId: this.rentalId, paymentAttempted: true });
    }
  }
}
